// src/pages/offlineSales/ReceiveCustomerPaymentPage.jsx
//
// Receive a lump payment from a customer without picking an invoice. The backend allocates it
// FIFO — opening balance first, then invoices oldest-first; any excess stays as customer credit.
// The table shows a live preview of that allocation before saving.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HandCoins, ArrowLeft, User, Landmark, Receipt, Loader2,
} from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import PaymentMethodPicker from '../../components/ui/PaymentMethodPicker/PaymentMethodPicker';
import { emptyCheque, getChequeErrorKey, toChequePayload } from '../../components/common/ChequeDetailsFields/ChequeDetailsFields';
import PaymentRecordEntry from '../../components/common/PaymentRecordEntry/PaymentRecordEntry';
import { getCustomers, getCustomer } from '../../services/api/offlineSales/customers';
import { getPaymentMethods } from '../../services/api/offlineSales/paymentMethods';
import { uploadOrderPaymentProof } from '../../services/api/offlineSales/orders';
import { previewCustomerPaymentAllocation, createCustomerPayment } from '../../services/api/offlineSales/customerPayments';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate } from '../../utils/dateUtils';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ReceiveCustomerPaymentPage.module.css';

const todayYMD = () => new Date().toISOString().split('T')[0];
const toCustomerOption = (c) => ({ value: c.id, label: c.phone ? `${c.name} (${c.phone})` : c.name });

const ReceiveCustomerPaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError } = useAlert();

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerId, setCustomerId] = useState(Number(searchParams.get('customerId')) || null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState({
    paymentMethodKey: '',
    amount: '',
    paymentDate: todayYMD(),
    reference: '',
    notes: '',
    proofUrl: null,
    cheque: emptyCheque(),
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showChequeErrors, setShowChequeErrors] = useState(false);
  const previewTimer = useRef(null);

  useEffect(() => {
    getPaymentMethods()
      .then((data) => setMethods((Array.isArray(data) ? data : []).filter((m) => m.key !== 'credit_note_apply')))
      .catch(() => setMethods([]));
  }, []);

  const searchCustomers = useCallback(async (term = '') => {
    setCustomerSearching(true);
    try {
      const result = await getCustomers({ search: term.trim(), limit: 20, page: 1 });
      setCustomerOptions((result.customers || [])
        .filter((c) => !c.isCashSales)
        .map(toCustomerOption));
    } catch {
      setCustomerOptions([]);
    } finally {
      setCustomerSearching(false);
    }
  }, []);

  useEffect(() => { searchCustomers(''); }, [searchCustomers]);

  // Selected customer is held separately and merged into the options, so a search result list
  // (which replaces customerOptions) can never drop it — e.g. when preselected via ?customerId=.
  useEffect(() => {
    if (!customerId || selectedCustomerOption?.value === customerId) return;
    getCustomer(customerId)
      .then((res) => {
        const c = res.customer || res;
        if (c?.id) setSelectedCustomerOption(toCustomerOption(c));
      })
      .catch(() => {});
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergedOptions = useMemo(() => (selectedCustomerOption
    ? [selectedCustomerOption, ...customerOptions.filter((o) => o.value !== selectedCustomerOption.value)]
    : customerOptions), [selectedCustomerOption, customerOptions]);

  const loadPreview = useCallback(async (id, amount) => {
    if (!id) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    try {
      setPreview(await previewCustomerPaymentAllocation(id, parseFloat(amount) || 0));
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToPreview'));
    } finally {
      setPreviewLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => loadPreview(customerId, form.amount), 400);
    return () => clearTimeout(previewTimer.current);
  }, [customerId, form.amount, loadPreview]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const isCheque = form.paymentMethodKey === 'cheque';

  const handleSubmit = async () => {
    if (!customerId) {
      showError(t('offlineSales.cpSelectCustomerFirst'));
      return;
    }
    if (!form.paymentMethodKey) {
      showError(t('offlineSales.cpSelectMethod'));
      return;
    }
    if (!(parseFloat(form.amount) > 0)) {
      showError(t('offlineSales.cpAmountRequired'));
      return;
    }
    const chequeErrorKey = isCheque ? getChequeErrorKey(form.cheque) : null;
    if (chequeErrorKey) {
      setShowChequeErrors(true);
      showError(t(chequeErrorKey));
      return;
    }

    setSaving(true);
    try {
      await createCustomerPayment({
        customerId,
        paymentMethodKey: form.paymentMethodKey,
        amount: parseFloat(form.amount),
        paymentDate: form.paymentDate,
        reference: form.reference || null,
        notes: form.notes || null,
        proofUrl: form.proofUrl || null,
        ...(isCheque ? { cheque: toChequePayload(form.cheque) } : {}),
      });
      success(t('offlineSales.cpSaved'));
      navigate(`/offline-sales/customers/${customerId}/statement`);
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'type',
      header: t('offlineSales.type'),
      render: (row) => (
        <span className={styles.typeCell}>
          {row.type === 'opening_balance' ? <Landmark size={14} /> : <Receipt size={14} />}
          {row.type === 'opening_balance' ? t('offlineSales.openingBalance') : (row.reference || '—')}
        </span>
      ),
    },
    {
      key: 'date',
      header: t('offlineSales.date'),
      render: (row) => formatDate(row.date),
    },
    {
      key: 'outstanding',
      header: t('offlineSales.cpOutstanding'),
      align: 'right',
      render: (row) => formatCurrency(row.outstanding),
    },
    {
      key: 'applied',
      header: t('offlineSales.cpApplied'),
      align: 'right',
      render: (row) => (row.applied > 0
        ? <span className={styles.appliedAmount}>{formatCurrency(row.applied)}</span>
        : <span className={tb.naText}>—</span>),
    },
    {
      key: 'remaining',
      header: t('offlineSales.cpRemaining'),
      align: 'right',
      render: (row) => formatCurrency(Math.max(0, row.outstanding - row.applied)),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.cpReceivePayment')}
        description={t('offlineSales.cpReceiveDescription')}
        icon={HandCoins}
        actions={
          <button className={styles.secondaryBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> {t('common.back')}
          </button>
        }
      />

      <div className={styles.layout}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>{t('offlineSales.cpPaymentDetails')}</h2>

          <div className={styles.formGroup}>
            <label><User size={13} /> {t('offlineSales.customer')} *</label>
            <Dropdown
              value={customerId}
              onChange={(v) => {
                const id = v ? Number(v) : null;
                setCustomerId(id);
                setSelectedCustomerOption(id ? mergedOptions.find((o) => o.value === id) || null : null);
              }}
              options={mergedOptions}
              onSearch={searchCustomers}
              loading={customerSearching}
              placeholder={t('offlineSales.selectCustomer')}
              searchPlaceholder={t('offlineSales.searchCustomers')}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('offlineSales.cpMethod')} *</label>
            <PaymentMethodPicker
              methods={methods}
              value={form.paymentMethodKey}
              onSelect={(key) => setForm((f) => ({ ...f, paymentMethodKey: key }))}
            />
          </div>

          {form.paymentMethodKey && (
            <PaymentRecordEntry
              index={0}
              payment={form}
              methods={methods}
              title={t('offlineSales.cpPaymentDetails')}
              onChange={(field, value) => setForm((f) => ({ ...f, [field]: value }))}
              onUploadProof={uploadOrderPaymentProof}
              enableCheque
              showChequeErrors={showChequeErrors}
              idPrefix="cp"
            />
          )}

          <div className={styles.formGroup}>
            <label>{t('offlineSales.cpNotes')}</label>
            <textarea className={styles.plainInput} rows={2} maxLength={1000} value={form.notes} onChange={set('notes')} />
          </div>

          <button type="button" className={styles.primaryBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 size={16} className={styles.spin} /> : <HandCoins size={16} />}
            {t('offlineSales.cpSave')}
          </button>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeaderRow}>
            <h2 className={styles.cardTitle}>{t('offlineSales.cpAllocationPreview')}</h2>
            {preview && (
              <span className={styles.outstandingTotal}>
                {t('offlineSales.totalOutstanding')}: {formatCurrency(preview.totalOutstanding)}
              </span>
            )}
          </div>
          <p className={styles.hint}>{t('offlineSales.cpFifoHint')}</p>

          {!customerId ? (
            <div className={styles.emptyHint}>{t('offlineSales.cpSelectCustomerFirst')}</div>
          ) : (
            <>
              <DataTable
                data={(preview?.items || []).map((i) => ({ ...i, id: `${i.type}-${i.id}` }))}
                columns={columns}
                loading={previewLoading && !preview}
                emptyMessage={t('offlineSales.cpNoOpenItems')}
                emptyDescription={t('offlineSales.cpNoOpenItemsDescription')}
              />
              {preview && (
                <div className={styles.previewFooter}>
                  {preview.unallocatedCredit > 0 && (
                    <span>{t('offlineSales.cpExistingCredit')}: {formatCurrency(preview.unallocatedCredit)}</span>
                  )}
                  {preview.unallocated > 0 && (
                    <span className={styles.unallocatedWarning}>
                      {t('offlineSales.cpWillBeCredit', { amount: formatCurrency(preview.unallocated) })}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ReceiveCustomerPaymentPage;
